import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, ExcelIcon, exportToExcel,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser, ViewIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = '/api/vehicles/counts';

const STATUS_TABS = [
  { key: '', label: 'Tümü' },
  { key: 'PENDING_APPROVAL', label: 'Onay Bekliyor' },
  { key: 'IN_PROGRESS', label: 'Sayım Devam' },
  { key: 'COMPLETED', label: 'Tamamlandı' },
  { key: 'REJECTED', label: 'Reddedildi' },
];

const STATUS_META = {
  PENDING_APPROVAL: { label: 'Onay Bekliyor', tone: 'wait' },
  IN_PROGRESS: { label: 'Sayım Devam', tone: 'wait' },
  COMPLETED: { label: 'Tamamlandı', tone: 'ok' },
  REJECTED: { label: 'Reddedildi', tone: 'bad' },
};

const PAGE_SIZE_DEFAULT = 20;

// Bu ekran OData değil; özel REST endpoint'i (/api/vehicles/counts) sayım özetleriyle
// birlikte tek seferde (take=500) veri döndürür. Bu yüzden useC360List yerine mevcut
// fetch korunur; görsel kabuk (ListPageShell + C360Table + Pagination) c360'a taşınır.
// Filtreleme/sıralama/sayfalama istemci tarafında yapılır.

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapResponse(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    pendingCount: pick(data, 'pendingCount', 'PendingCount') ?? 0,
    inProgressCount: pick(data, 'inProgressCount', 'InProgressCount') ?? 0,
    completedCount: pick(data, 'completedCount', 'CompletedCount') ?? 0,
    rejectedCount: pick(data, 'rejectedCount', 'RejectedCount') ?? 0,
    varianceTotal: pick(data, 'varianceTotal', 'VarianceTotal') ?? 0,
  };
}

function statusBadgeFor(status) {
  const meta = STATUS_META[status] || { label: status || '-', tone: 'neutral' };
  return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
}

function statusLabelFor(status) {
  return (STATUS_META[status] || { label: status || '-' }).label;
}

function variancePill(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) {
    return <span style={{ color: '#64748b', fontSize: 12 }}>0</span>;
  }
  const positive = n > 0;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 999, fontWeight: 700, fontSize: 12,
      background: positive ? 'rgba(0, 200, 117, 0.12)' : 'rgba(226, 68, 92, 0.12)',
      color: positive ? '#0f5132' : '#7f1d1d',
      border: `1px solid ${positive ? 'rgba(0, 200, 117, 0.30)' : 'rgba(226, 68, 92, 0.30)'}`,
    }}>
      {positive ? '+' : ''}{n}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

function toIsoStart(d) { if (!d) return ''; try { return new Date(`${d}T00:00:00`).toISOString(); } catch { return ''; } }
function toIsoEnd(d) {
  if (!d) return '';
  try { const x = new Date(`${d}T00:00:00`); x.setDate(x.getDate() + 1); return x.toISOString(); } catch { return ''; }
}

function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Araç Sayım kaydını matbu form (PDF/yazdır) olarak üretir. Harici PDF kütüphanesi
// kullanmadan yeni bir pencerede yazdırılabilir HTML belge açar; kullanıcı "PDF olarak
// kaydet" ile PDF çıktısı veya doğrudan yazıcıdan çıktı alabilir.
function buildPrintDocument(row, lines) {
  const safeLines = Array.isArray(lines) ? lines : [];
  const sdm = escapeHtml(pick(row, 'sdmName', 'SdmName') || '-');
  const plate = escapeHtml(pick(row, 'plate', 'Plate') || '-');
  const status = escapeHtml(statusLabelFor(pick(row, 'status', 'Status')));
  const requestedAt = escapeHtml(formatDate(pick(row, 'requestedAt', 'RequestedAt')));
  const approvedAt = escapeHtml(formatDate(pick(row, 'approvedAt', 'ApprovedAt')));
  const counted = pick(row, 'countedItems', 'CountedItems') ?? 0;
  const totalItems = pick(row, 'totalItems', 'TotalItems') ?? 0;
  const varianceCount = pick(row, 'varianceCount', 'VarianceCount') ?? 0;
  const totalVariance = Number(pick(row, 'totalVariance', 'TotalVariance') ?? 0);
  const rejectReason = pick(row, 'rejectReason', 'RejectReason');

  let sumSystem = 0;
  let sumCounted = 0;
  let sumVariance = 0;
  const bodyRows = safeLines.map((line, idx) => {
    const sys = Number(pick(line, 'systemCases', 'SystemCases') ?? 0);
    const cnt = pick(line, 'countedCases', 'CountedCases');
    const varv = pick(line, 'variance', 'Variance');
    const cntNum = cnt === null || cnt === undefined ? null : Number(cnt);
    const varNum = varv === null || varv === undefined ? null : Number(varv);
    sumSystem += Number.isFinite(sys) ? sys : 0;
    if (cntNum !== null && Number.isFinite(cntNum)) sumCounted += cntNum;
    if (varNum !== null && Number.isFinite(varNum)) sumVariance += varNum;
    const varClass = varNum > 0 ? 'pos' : (varNum < 0 ? 'neg' : '');
    return `<tr>
      <td class="num">${idx + 1}</td>
      <td>${escapeHtml(pick(line, 'productCode', 'ProductCode') || '-')}</td>
      <td>${escapeHtml(pick(line, 'productName', 'ProductName') || '-')}</td>
      <td class="num">${escapeHtml(sys)}</td>
      <td class="num">${cntNum === null ? '-' : escapeHtml(cntNum)}</td>
      <td class="num ${varClass}">${varNum === null ? '-' : (varNum > 0 ? '+' : '') + escapeHtml(varNum)}</td>
    </tr>`;
  }).join('');

  const totalVarClass = totalVariance > 0 ? 'pos' : (totalVariance < 0 ? 'neg' : '');
  const printedAt = escapeHtml(formatDate(new Date().toISOString()));

  const infoCell = (label, value) => `<div class="info-cell"><span class="info-label">${label}</span><span class="info-value">${value}</span></div>`;

  return `<!DOCTYPE html>
<html lang="tr">
<head>
<meta charset="utf-8" />
<title>Araç Sayım Formu - ${plate}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1f2937; font-size: 12px; padding: 24px; }
  .doc-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1f2937; padding-bottom: 12px; margin-bottom: 16px; }
  .doc-title { font-size: 20px; font-weight: 800; letter-spacing: 0.5px; margin: 0; }
  .doc-sub { font-size: 11px; color: #6b7280; margin-top: 4px; }
  .doc-meta { text-align: right; font-size: 11px; color: #6b7280; }
  .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 16px; margin-bottom: 18px; }
  .info-cell { display: flex; flex-direction: column; border: 1px solid #e5e7eb; border-radius: 6px; padding: 6px 10px; }
  .info-label { font-size: 9px; text-transform: uppercase; color: #9ca3af; font-weight: 700; letter-spacing: 0.4px; }
  .info-value { font-size: 13px; font-weight: 600; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  th, td { border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; }
  th { background: #f3f4f6; font-size: 10px; text-transform: uppercase; letter-spacing: 0.3px; color: #374151; }
  td.num, th.num { text-align: right; }
  tfoot td { font-weight: 800; background: #f9fafb; }
  .pos { color: #15803d; font-weight: 700; }
  .neg { color: #b91c1c; font-weight: 700; }
  .reject { margin-top: 14px; border: 1px solid #fca5a5; background: #fef2f2; border-radius: 6px; padding: 8px 10px; font-size: 11px; }
  .reject b { color: #b91c1c; }
  .signatures { display: flex; justify-content: space-between; margin-top: 48px; gap: 40px; }
  .sign-box { flex: 1; text-align: center; }
  .sign-line { border-top: 1px solid #6b7280; margin-top: 40px; padding-top: 6px; font-size: 11px; color: #374151; }
  @media print { body { padding: 0; } @page { margin: 16mm; } }
</style>
</head>
<body>
  <div class="doc-header">
    <div>
      <h1 class="doc-title">ARAÇ SAYIM FORMU</h1>
      <div class="doc-sub">Saha temsilcisi araç stok sayım belgesi</div>
    </div>
    <div class="doc-meta">Yazdırma: ${printedAt}</div>
  </div>

  <div class="info-grid">
    ${infoCell('Plaka', plate)}
    ${infoCell('SDM / Temsilci', sdm)}
    ${infoCell('Durum', status)}
    ${infoCell('Talep Tarihi', requestedAt)}
    ${infoCell('Onay Tarihi', approvedAt)}
    ${infoCell('Sayılan / Toplam', `${escapeHtml(counted)} / ${escapeHtml(totalItems)}`)}
    ${infoCell('Farklı SKU', escapeHtml(varianceCount))}
    ${infoCell('Toplam Fark', `<span class="${totalVarClass}">${totalVariance > 0 ? '+' : ''}${escapeHtml(totalVariance)}</span>`)}
  </div>

  <table>
    <thead>
      <tr>
        <th class="num">#</th>
        <th>Ürün Kodu</th>
        <th>Ürün Adı</th>
        <th class="num">Mevcut Stok</th>
        <th class="num">Sayım Sonucu</th>
        <th class="num">Fark</th>
      </tr>
    </thead>
    <tbody>
      ${bodyRows || '<tr><td colspan="6" style="text-align:center;color:#9ca3af;padding:24px;">Satır bulunamadı</td></tr>'}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="3">TOPLAM</td>
        <td class="num">${escapeHtml(sumSystem)}</td>
        <td class="num">${escapeHtml(sumCounted)}</td>
        <td class="num ${sumVariance > 0 ? 'pos' : (sumVariance < 0 ? 'neg' : '')}">${sumVariance > 0 ? '+' : ''}${escapeHtml(sumVariance)}</td>
      </tr>
    </tfoot>
  </table>

  ${rejectReason ? `<div class="reject"><b>Red Gerekçesi:</b> ${escapeHtml(rejectReason)}</div>` : ''}

  <div class="signatures">
    <div class="sign-box"><div class="sign-line">Sayımı Yapan</div></div>
    <div class="sign-box"><div class="sign-line">Onaylayan (Şef)</div></div>
  </div>
</body>
</html>`;
}

function badge(label, value, tone) {
  const tones = {
    blue: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8', dot: 'var(--accent)' },
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
    red: { bg: 'rgba(226, 68, 92, 0.12)', border: 'rgba(226, 68, 92, 0.30)', color: '#7f1d1d', dot: '#dc2626' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function VehicleCountsPage() {
  usePageMeta('Araç Sayım Raporları', 'Saha temsilcisinin araç sayım talepleri ve fark raporları');

  const [statusFilter, setStatusFilter] = useState('');
  const [plate, setPlate] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pendingCount: 0, inProgressCount: 0, completedCount: 0, rejectedCount: 0, varianceTotal: 0 });
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sorting, setSorting] = useState([{ id: 'requestedAt', desc: true }]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selected, setSelected] = useState(null);

  const [detailRow, setDetailRow] = useState(null);
  const [detailLines, setDetailLines] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (plate.trim()) params.set('plate', plate.trim());
      const isoFrom = toIsoStart(dateFrom);
      const isoTo = toIsoEnd(dateTo);
      if (isoFrom) params.set('from', isoFrom);
      if (isoTo) params.set('to', isoTo);
      params.set('take', '500');

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapResponse(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({
        pendingCount: data.pendingCount,
        inProgressCount: data.inProgressCount,
        completedCount: data.completedCount,
        rejectedCount: data.rejectedCount,
        varianceTotal: data.varianceTotal,
      });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, plate, dateFrom, dateTo, showToast]);

  useEffect(() => { load(); }, [load]);

  const openDetail = useCallback(async (row) => {
    setSelected(row);
    setDetailRow(row);
    setDetailLines(null);
    setDetailLoading(true);
    try {
      const id = pick(row, 'id', 'Id');
      const res = await fetch(`${API_BASE}/${id}/details`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Satırlar alınamadı (${res.status})`);
      const payload = await res.json();
      const data = pick(payload, 'Data', 'data') ?? payload;
      setDetailLines(pick(data, 'items', 'Items') ?? []);
    } catch (err) {
      showToast('error', err.message || 'Satırlar alınamadı');
      setDetailLines([]);
    } finally {
      setDetailLoading(false);
    }
  }, [showToast]);

  const closeDetail = () => { setDetailRow(null); setDetailLines(null); };

  const handlePrint = useCallback(() => {
    if (!detailRow) return;
    if (detailLoading) { showToast('info', 'Satırlar yükleniyor, lütfen bekleyin...'); return; }
    const html = buildPrintDocument(detailRow, detailLines || []);
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { showToast('error', 'Açılır pencere engellendi. Lütfen pop-up iznini açın.'); return; }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    // İçerik ve fontlar yerleşsin diye küçük bir gecikme sonrası yazdır.
    win.setTimeout(() => { win.print(); }, 300);
  }, [detailRow, detailLines, detailLoading, showToast]);

  // İstemci tarafı: serbest metin araması (SDM / plaka / red gerekçesi).
  const filteredRows = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    if (!needle) return rows;
    return rows.filter((row) => {
      const fields = [
        pick(row, 'sdmName', 'SdmName'),
        pick(row, 'plate', 'Plate'),
        pick(row, 'rejectReason', 'RejectReason'),
      ];
      return fields.some((v) => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    });
  }, [rows, searchText]);

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('requestedAt', {
      header: 'Talep', enableColumnFilter: false,
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('sdmName', {
      header: 'SDM', enableColumnFilter: false,
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('plate', {
      header: 'Plaka', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('status', {
      header: 'Durum', enableColumnFilter: false,
      cell: (info) => statusBadgeFor(info.getValue()),
    }),
    columnHelper.display({
      id: 'countedItems', header: 'Sayılan',
      cell: ({ row }) => {
        const counted = pick(row.original, 'countedItems', 'CountedItems') ?? 0;
        const total = pick(row.original, 'totalItems', 'TotalItems') ?? 0;
        return `${counted} / ${total}`;
      },
    }),
    columnHelper.accessor('totalVariance', {
      header: 'Fark', enableColumnFilter: false,
      cell: (info) => variancePill(info.getValue()),
    }),
    columnHelper.accessor('varianceCount', {
      header: 'Farklı SKU', enableColumnFilter: false,
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.accessor('detailCount', {
      header: 'Satır', enableColumnFilter: false,
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Detay" aria-label="Detay" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [openDetail]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: page - 1, pageSize } },
    onSortingChange: (updater) => { setSorting(updater); setPage(1); },
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex: page - 1, pageSize }) : updater;
      setPage(next.pageIndex + 1);
      setPageSize(next.pageSize);
    },
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Talep', valueGetter: ({ row }) => formatDate(pick(row, 'requestedAt', 'RequestedAt')) },
        { headerName: 'SDM', valueGetter: ({ row }) => pick(row, 'sdmName', 'SdmName') || '' },
        { headerName: 'Plaka', valueGetter: ({ row }) => pick(row, 'plate', 'Plate') || '' },
        { headerName: 'Durum', valueGetter: ({ row }) => statusLabelFor(pick(row, 'status', 'Status')) },
        { headerName: 'Sayılan', valueGetter: ({ row }) => pick(row, 'countedItems', 'CountedItems') ?? 0 },
        { headerName: 'Toplam', valueGetter: ({ row }) => pick(row, 'totalItems', 'TotalItems') ?? 0 },
        { headerName: 'Fark', valueGetter: ({ row }) => pick(row, 'totalVariance', 'TotalVariance') ?? 0 },
        { headerName: 'Farklı SKU', valueGetter: ({ row }) => pick(row, 'varianceCount', 'VarianceCount') ?? 0 },
        { headerName: 'Satır', valueGetter: ({ row }) => pick(row, 'detailCount', 'DetailCount') ?? 0 },
      ];
      if (filteredRows.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredRows, cols, `arac-sayim_${dateStr}`);
      showToast('success', `${filteredRows.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      {STATUS_TABS.map((tab) => (
        <button
          key={tab.key || 'all'}
          type="button"
          onClick={() => setStatusFilter(tab.key)}
          style={{ ...(statusFilter === tab.key ? s.btnPrimary : s.btnSecondary), padding: '6px 12px', fontSize: 13 }}
        >
          {tab.label}
        </button>
      ))}
      <input
        style={{ ...s.filterInput, width: 110 }}
        type="text"
        placeholder="Plaka"
        value={plate}
        onChange={(e) => setPlate(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
      />
      <input style={{ ...s.filterInput, width: 140 }} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
      <input style={{ ...s.filterInput, width: 140 }} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      <input
        style={s.filterInput}
        type="search"
        placeholder="SDM / red gerekçesi..."
        value={searchText}
        onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
      />
      <button style={s.btnSecondary} onClick={load}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const headerActions = (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
      {badge('Onay Bekliyor', counts.pendingCount, 'orange')}
      {badge('Sayım Devam', counts.inProgressCount, 'blue')}
      {badge('Tamamlanan', counts.completedCount, 'green')}
      {badge('Reddedilen', counts.rejectedCount, 'red')}
      {badge('Toplam Fark', counts.varianceTotal, 'gray')}
    </div>
  );

  const footerNode = (
    <Pagination
      currentPage={page}
      totalCount={filteredRows.length}
      pageSize={pageSize}
      rowCount={table.getRowModel().rows.length}
      onPageChange={setPage}
      onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Araç Sayım Raporları"
        subtitle="Saha temsilcisinin günlük araç sayımı için açtığı talepler. Şef onayı sonrası sayım yapılır; fark raporu otomatik üretilir."
        actions={headerActions}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {detailRow && (
        <div style={s.overlay} onClick={closeDetail}>
          <div style={{ ...s.modal, maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {pick(detailRow, 'sdmName', 'SdmName')} • {pick(detailRow, 'plate', 'Plate')}
              </h3>
              <button type="button" style={s.closeBtn} onClick={closeDetail}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Durum</label>
                  <div>{statusBadgeFor(pick(detailRow, 'status', 'Status'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Toplam Fark</label>
                  <div>{variancePill(pick(detailRow, 'totalVariance', 'TotalVariance'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Talep Tarihi</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'requestedAt', 'RequestedAt'))}</div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Onay Tarihi</label>
                  <div style={s.inputView}>{formatDate(pick(detailRow, 'approvedAt', 'ApprovedAt'))}</div>
                </div>
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Sayılan / Toplam</label>
                  <div style={s.inputView}>
                    {pick(detailRow, 'countedItems', 'CountedItems') ?? 0} / {pick(detailRow, 'totalItems', 'TotalItems') ?? 0}
                  </div>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Farklı SKU</label>
                  <div style={s.inputView}>{pick(detailRow, 'varianceCount', 'VarianceCount') ?? 0}</div>
                </div>
              </div>
              {pick(detailRow, 'rejectReason', 'RejectReason') && (
                <div style={s.formGroup}>
                  <label style={s.label}>Red Gerekçesi</label>
                  <div style={{ ...s.textareaView, background: 'rgba(226, 68, 92, 0.06)' }}>
                    {pick(detailRow, 'rejectReason', 'RejectReason')}
                  </div>
                </div>
              )}
              <div style={s.formGroup}>
                <label style={s.label}>Satırlar ({detailLines?.length ?? '...'})</label>
                {detailLoading && <div style={{ color: '#6b7280', fontSize: 13 }}>Yükleniyor...</div>}
                {!detailLoading && detailLines && (
                  <div style={{
                    border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
                    maxHeight: 320, overflowY: 'auto',
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ background: '#f9fafb' }}>
                        <tr>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Ürün</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Sistem</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Sayılan</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Birim</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Fark</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailLines.length === 0 && (
                          <tr><td colSpan="5" style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Satır yok</td></tr>
                        )}
                        {detailLines.map((line, idx) => (
                          <tr key={pick(line, 'id', 'Id') || idx} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                            <td style={{ padding: '8px 12px' }}>
                              <div style={{ fontWeight: 600 }}>{pick(line, 'productName', 'ProductName') || '-'}</div>
                              {pick(line, 'productCode', 'ProductCode') && (
                                <div style={{ fontSize: 11, color: '#6b7280' }}>{pick(line, 'productCode', 'ProductCode')}</div>
                              )}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6b7280' }}>
                              {pick(line, 'systemCases', 'SystemCases') ?? '-'}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                              {pick(line, 'countedCases', 'CountedCases') ?? '-'}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6b7280' }}>
                              {(() => {
                                const q = pick(line, 'countedQty', 'CountedQty');
                                const u = pick(line, 'unit', 'Unit');
                                return q != null ? `${q}${u ? ` ${u}` : ''}` : '-';
                              })()}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                              {variancePill(pick(line, 'variance', 'Variance'))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeDetail}>Kapat</button>
              <button type="button" style={s.btnPrimary} onClick={handlePrint} disabled={detailLoading}>
                Yazdır / PDF
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default VehicleCountsPage;
